<template>
  <fullscreen-dialog
    v-model="model"
    :title="user ? toLatin(user.user_name) : ''"
    :subtitle="`ID: ${user ? user.user_id : ''}`"
    hide-footer
  >
    <template #default>
      <v-card-text>
        <div v-if="!user">
          {{ $t("no_data") }}
        </div>

        <div v-else>
          <!-- Summary Card -->
          <v-card outlined class="mb-4">
            <v-card-text>
              <v-row dense justify="space-between">
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("orders") }}</div>
                  <div class="text-h6 primary--text">
                    {{ filteredRows.length }}
                  </div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("orders_total") }}
                  </div>
                  <div class="text-h6">{{ formatAmount(filteredOrdersTotal) }}</div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("spent_on_credits") }}
                  </div>
                  <div class="text-h6 error--text">
                    {{ formatAmount(filteredCreditsSpent) }}
                  </div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">
                    {{ $t("credit_balance") }}
                  </div>
                  <div class="text-h6 success--text">
                    {{ formatAmount(user.totals && user.totals.current_credit_balance) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Filters row -->
          <v-row align="center" class="mb-2">
            <v-col cols="auto">
              <month-picker v-model="selectedMonth" color="white"></month-picker>
            </v-col>
            <v-col cols="12" sm="5" md="4">
              <autocomplete
                v-model="selectedRestaurants"
                :items="restaurantNames"
                :label="$t('restaurant')"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                :hide-details="false"
                color="white"
              />
            </v-col>
            <v-col cols="3" md="2">
              <text-field
                v-model="search"
                kind="search"
                :label="$t('search')"
                clearable
                color="white"
              ></text-field>
            </v-col>
            <v-col align="end">
              <v-row no-gutters align="center" justify="end">
                <excel-button class="mr-2" @click="exportToExcel"></excel-button>
                <print-button @click="printReport"></print-button>
              </v-row>
            </v-col>
          </v-row>

          <!-- Orders Table -->
          <v-data-table
            :headers="headers"
            :items="filteredRowsWithTotal"
            hide-default-footer
            disable-pagination
            class="striped-table ordera-box-shadow"
          >
            <template v-slot:item.num="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.num }}</span>
            </template>

            <template v-slot:item.date="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
              </span>
            </template>

            <template v-slot:item.restaurant="{ item }">
              <span v-if="!item.isTotal">{{ toLatin(item.restaurant) }}</span>
            </template>

            <template v-slot:item.meal_name="{ item }">
              <span v-if="!item.isTotal">{{ toLatin(item.meal_name) }}</span>
            </template>

            <template v-slot:item.total="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold primary--text' : ''">
                {{ formatAmount(item.total) }}
              </span>
            </template>

            <template v-slot:item.credits_spent="{ item }">
              <span
                v-if="!item.isTotal"
                :class="item.credits_spent < 0 ? 'error--text' : ''"
              >
                {{ item.credits_spent !== 0 ? formatAmount(item.credits_spent) : "" }}
              </span>
              <span v-else class="font-weight-bold error--text">
                {{ formatAmount(filteredCreditsSpentRaw) }}
              </span>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";

export default {
  components: {
    FullscreenDialog,
  },
  mixins: [hasModel, hasExcel, hasPrinting],
  props: {
    user: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    selectedMonth: null,
    search: "",
    selectedRestaurants: [],
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px" },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("restaurant"), value: "restaurant", sortable: false },
        { text: this.$t("meal"), value: "meal_name", sortable: false },
        { text: this.$t("amount"), value: "total", sortable: false, align: "right" },
        {
          text: this.$t("spent_on_credits"),
          value: "credits_spent",
          sortable: false,
          align: "right",
        },
      ];
    },
    restaurantNames() {
      const orders = this.user?.orders || [];
      return [...new Set(orders.map((o) => o.restaurant).filter(Boolean))];
    },
    allRows() {
      const orders = this.user?.orders || [];
      return orders
        .filter((o) => o && o.id)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((o, index) => ({
          id: o.id,
          date: o.date,
          restaurant: o.restaurant,
          meal_name: (o.items || []).map((i) => i.name).join(", "),
          total: (o.total_cents || 0) / 100,
          credits_spent: o.credits_spent || 0,
          isTotal: false,
          num: index + 1,
        }));
    },
    filteredRows() {
      let rows = this.allRows;

      if (this.selectedMonth) {
        const [year, month] = this.selectedMonth.split("-").map(Number);
        rows = rows.filter((r) => {
          if (!r.date) return false;
          const d = new Date(r.date);
          return d.getFullYear() === year && d.getMonth() + 1 === month;
        });
      }

      if (
        this.selectedRestaurants.length > 0 &&
        this.selectedRestaurants.length < this.restaurantNames.length
      ) {
        rows = rows.filter((r) => this.selectedRestaurants.includes(r.restaurant));
      }

      if (this.search) {
        const q = this.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            (r.meal_name || "").toLowerCase().includes(q) ||
            (r.restaurant || "").toLowerCase().includes(q) ||
            this.localeDate(r.date).toLowerCase().includes(q)
        );
      }

      return rows.map((r, i) => ({ ...r, num: i + 1 }));
    },
    filteredRowsWithTotal() {
      const rows = [...this.filteredRows];
      if (rows.length > 0) {
        rows.push({
          id: "total",
          num: "",
          date: "",
          restaurant: "",
          meal_name: "",
          total: this.filteredOrdersTotal,
          credits_spent: this.filteredCreditsSpentRaw,
          isTotal: true,
        });
      }
      return rows;
    },
    filteredOrdersTotal() {
      return this.filteredRows.reduce((sum, r) => sum + r.total, 0);
    },
    // Raw signed sum (negative = spent)
    filteredCreditsSpentRaw() {
      return this.filteredRows.reduce((sum, r) => sum + r.credits_spent, 0);
    },
    // Absolute value for summary display
    filteredCreditsSpent() {
      return Math.abs(this.filteredCreditsSpentRaw);
    },
    monthName() {
      if (!this.selectedMonth) return "";
      return this.localeDate(this.selectedMonth + "-01", {
        month: "long",
        year: "numeric",
      });
    },
  },
  watch: {
    user() {
      this.resetFilters();
    },
    model(val) {
      if (val) {
        this.resetFilters();
      }
    },
  },
  methods: {
    resetFilters() {
      this.selectedRestaurants = [...this.restaurantNames];
      this.search = "";
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined || amount === "") return "0.00";
      return (parseFloat(amount) || 0).toFixed(2) + " " + (this.restaurantCurrency || "");
    },
    exportToExcel() {
      const exportItems = this.filteredRows.map((item) => ({
        num: item.num,
        date: this.localeDate(item.date),
        restaurant: this.toLatin(item.restaurant),
        meal_name: this.toLatin(item.meal_name),
        total: item.total.toFixed(2),
        credits_spent: item.credits_spent !== 0 ? item.credits_spent.toFixed(2) : "",
      }));
      const totalRow = {
        num: "",
        date: this.$t("total"),
        restaurant: "",
        meal_name: "",
        total: this.filteredOrdersTotal.toFixed(2),
        credits_spent: this.filteredCreditsSpentRaw.toFixed(2),
      };
      this.downloadExcel({
        fileName: `${this.user.user_name}${this.monthName ? "_" + this.monthName : ""}.xlsx`,
        sheets: [
          {
            name: this.user.user_name,
            headers: this.headers,
            items: [...exportItems, totalRow],
            title: this.user.user_name,
            subtitle: this.monthName,
          },
        ],
      });
    },
    printReport() {
      const printItems = this.filteredRows.map((item) => ({
        num: item.num,
        date: this.localeDate(item.date),
        restaurant: this.toLatin(item.restaurant),
        meal_name: this.toLatin(item.meal_name),
        total: item.total.toFixed(2),
        credits_spent: item.credits_spent !== 0 ? item.credits_spent.toFixed(2) : "",
      }));
      const totalRow = {
        num: "",
        date: this.$t("total"),
        restaurant: "",
        meal_name: "",
        total: this.filteredOrdersTotal.toFixed(2),
        credits_spent: this.filteredCreditsSpentRaw.toFixed(2),
      };
      this.printDocument({
        title: this.user.user_name,
        subtitle: this.monthName,
        tables: [
          {
            headers: this.headers,
            items: [...printItems, totalRow],
          },
        ],
      });
    },
  },
};
</script>
